import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../AuthContext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Chart } from "primereact/chart";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import {
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
  FaFileInvoiceDollar,
  FaPrint,
  FaFileInvoice,
  FaDollarSign,
  FaWallet,
  FaCalendar,
  FaCalendarCheck,
  FaPlusCircle,
  FaMinusCircle,
  FaMoneyBillWave,
  FaChartLine,
  FaWhatsapp,
  FaExchangeAlt,
  FaArrowRight,
} from "react-icons/fa";

const Relatorios = () => {
  const { user } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [caixas, setCaixas] = useState([]);
  const [despesas, setDespesas] = useState([]);
  const [agendamentos, setAgendamentos] = useState([]);
  const [expandedCard, setExpandedCard] = useState(null);

  // Filtro
  const [mostrarFiltro, setMostrarFiltro] = useState(false);

  // Estados para filtros de data
  const [dataInicio, setDataInicio] = useState(() => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  });

  const [dataFim, setDataFim] = useState(() => {
    const hoje = new Date();
    return new Date(
      hoje.getFullYear(),
      hoje.getMonth(),
      hoje.getDate(),
      23,
      59,
      59,
    );
  });

  // Função para buscar agendamentos (status_reserva=confirmada)
  const fetchAgendamentos = async () => {
    try {
      if (!user?.token) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await fetch(
        "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agendamentos",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "include",
        },
      );

      if (!response.ok) throw new Error("Erro ao buscar agendas");
      const data = await response.json();

      console.log("Agendas = ", data.data);

      if (data.success && Array.isArray(data.data)) {
        setAgendamentos(data.data);
      } else {
        setAgendamentos([]);
      }
    } catch (error) {
      console.error("Erro ao buscar agendamentos:", error);
      setAgendamentos([]);
    }
  };

  // Função para buscar caixas (status=fechado)
  const fetchCaixas = async () => {
    try {
      if (!user?.token) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await fetch(
        "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/caixas",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "include",
        },
      );

      if (!response.ok) throw new Error("Erro ao buscar caixas");
      const data = await response.json();

      console.log("Caixas = ", data.data);

      if (data.success && Array.isArray(data.data)) {
        setCaixas(data.data);
      } else {
        setCaixas([]);
      }
    } catch (error) {
      console.error("Erro ao buscar caixas:", error);
      setCaixas([]);
    }
  };

  // Função para buscar despesas
  const fetchDespesas = async () => {
    try {
      if (!user?.token) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await fetch(
        "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/despesas",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "include",
        },
      );

      if (!response.ok) throw new Error("Erro ao buscar despesas");
      const data = await response.json();

      console.log("Despesas = ", data.data);

      if (data.success && Array.isArray(data.data)) {
        setDespesas(data.data);
      } else {
        setDespesas([]);
      }
    } catch (error) {
      console.error("Erro ao buscar despesas:", error);
      setDespesas([]);
    }
  };

  // Função para buscar todos os dados
  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchAgendamentos(), fetchCaixas(), fetchDespesas()]);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Configurar intervalo de atualização
  useEffect(() => {
    fetchAllData();
    const intervalId = setInterval(fetchAllData, 300000);
    return () => clearInterval(intervalId);
  }, []);

  // Função para filtrar dados por data
  const filtrarPorData = (dados, campoData) => {
    if (!dataInicio || !dataFim) return dados;

    const inicio = new Date(dataInicio);
    inicio.setHours(0, 0, 0, 0);

    const fim = new Date(dataFim);
    fim.setHours(23, 59, 59, 999);

    return dados.filter((item) => {
      if (!item[campoData]) return false;

      let dataItemStr = item[campoData];

      // Trata formatos:
      // 1) 2025-12-09
      // 2) 2025-12-09T11:57:49
      if (dataItemStr.includes("T")) {
        dataItemStr = dataItemStr.split("T")[0];
      } else if (dataItemStr.includes(" ")) {
        dataItemStr = dataItemStr.split(" ")[0];
      }

      const [ano, mes, dia] = dataItemStr.split("-").map(Number);
      const dataItem = new Date(ano, mes - 1, dia);
      dataItem.setHours(0, 0, 0, 0);

      return dataItem >= inicio && dataItem <= fim;
    });
  };

  // Dados filtrados
  const agendamentosFiltrados = useMemo(
    () => filtrarPorData(agendamentos, "data_atendimento"),
    [agendamentos, dataInicio, dataFim],
  );

  const caixasFiltrados = useMemo(
    () => filtrarPorData(caixas, "data_fechamento"),
    [caixas, dataInicio, dataFim],
  );

  const despesasFiltradas = useMemo(() => {
    return filtrarPorData(
      despesas.filter((d) => d.ativo === "1" && d.data_cadastro),
      "data_cadastro",
    );
  }, [despesas, dataInicio, dataFim]);

  // Estatísticas
  const totalReservas = agendamentosFiltrados.filter(
    (agenda) =>
      agenda.status === "agendado" && agenda.status_reserva === "pendente",
  ).length;
  const totalAgendas = agendamentosFiltrados.filter(
    (agenda) =>
      agenda.status === "confirmado" && agenda.status_reserva === "confirmada",
  ).length;
  //const totalConfirmados = agendamentosFiltrados.filter(agenda => agenda.status === "realizado" && agenda.status_reserva === "confirmada").length;
  const totalPendentes = caixasFiltrados.filter(
    (caixa) => caixa.status === "aberto",
  ).length;
  
  const totalFinalizados = caixasFiltrados.filter(
    (caixa) =>
      caixa.status === "fechado" && caixa.status_pagamento === "pago_total",
  ).length;

  // Calcular total da carteira
  const totalCarteira = () => {
    return carteiraFiltrados.reduce((total, item) => {
      if (!Array.isArray(item?.pagamentos)) return total;

      const valorCarteira = item.pagamentos
        .filter(
          (p) => p.forma_pagamento_descricao?.toLowerCase() === "carteira",
        )
        .reduce((sum, p) => sum + (parseFloat(p.valor_pago) || 0), 0);

      return total + valorCarteira;
    }, 0);
  };

  // Calcular receitas totais
  const totalReceitas = useMemo(
    () =>
      caixasFiltrados
        .filter((caixa) => caixa.status === "fechado")
        .reduce(
          (total, caixa) => total + (parseFloat(caixa.valor_pago) || 0),
          0,
        ),
    [caixasFiltrados],
  );

  // Calcular despesas totais
  const totalDespesas = useMemo(
    () =>
      despesasFiltradas
        .filter((d) => d.ativo === "1")
        .reduce(
          (total, despesa) => total + (parseFloat(despesa.valor) || 0),
          0,
        ),
    [despesasFiltradas],
  );

  // Saldo Final
  const saldoFinal = totalReceitas - totalDespesas;

  // Filtrar pagamentos com carteira
  const carteiraFiltrados = useMemo(() => {
    if (!Array.isArray(caixasFiltrados)) return [];

    return caixasFiltrados.filter((item) => {
      if (!Array.isArray(item?.pagamentos)) return false;

      return item.pagamentos.some(
        (p) => p.forma_pagamento_descricao?.toLowerCase() === "carteira",
      );
    });
  }, [caixasFiltrados]);

  const totalCarteiraValor = useMemo(() => {
    if (!Array.isArray(carteiraFiltrados)) return 0;

    return carteiraFiltrados.reduce((total, item) => {
      if (!Array.isArray(item?.pagamentos)) return total;

      // Somar apenas os pagamentos com "Carteira" (atenção ao case)
      const pagamentosCarteira = item.pagamentos.filter((p) => {
        const descricao = p.forma_pagamento_descricao?.toLowerCase().trim();
        return descricao === "carteira";
      });

      const valorCarteira = pagamentosCarteira.reduce((sum, p) => {
        return sum + (parseFloat(p.valor) || 0);
      }, 0);

      return total + valorCarteira;
    }, 0);
  }, [carteiraFiltrados]);

  const totalPagamentosCarteira = useMemo(() => {
    if (!Array.isArray(carteiraFiltrados)) return 0;

    return carteiraFiltrados.reduce((total, item) => {
      if (!Array.isArray(item?.pagamentos)) return total;

      const pagamentosCarteira = item.pagamentos.filter((p) => {
        const descricao = p.forma_pagamento_descricao?.toLowerCase().trim();
        return descricao === "carteira";
      });

      return total + pagamentosCarteira.length;
    }, 0);
  }, [carteiraFiltrados]);

  // Atualizar o quadro "FINALIZADOS CARTEIRA"
  <div
    style={{
      backgroundColor: "#fdedec",
      border: "2px solid #e74c3c",
      borderRadius: "6px",
      padding: "12px",
      textAlign: "center",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "5px",
        marginBottom: "5px",
      }}
    >
      <FaWallet style={{ color: "#e74c3c", fontSize: "0.9rem" }} />
      <span
        style={{
          fontSize: "0.85rem",
          fontWeight: "600",
          color: "#e74c3c",
        }}
      >
        VALOR CARTEIRA
      </span>
    </div>
    <div
      style={{
        fontSize: "1.3rem",
        fontWeight: "bold",
        color: "#e74c3c",
      }}
    >
      {totalCarteira()}
    </div>
  </div>;

  // Calcular receitas por mês para gráfico
  const calcularReceitasPorMes = (caixas) => {
    const receitaPorMes = Array(12).fill(0);

    caixas
      .filter((caixa) => caixa.status === "fechado")
      .forEach((caixa) => {
        if (caixa.data_atendimento && caixa.valor_pago) {
          try {
            let dataStr = caixa.data_atendimento;

            // trata '2026-01-24', '2026-01-24T00:00:00'
            if (dataStr.includes("T")) {
              dataStr = dataStr.split("T")[0];
            }

            const [ano, mes, dia] = dataStr.split("-").map(Number);
            const data = new Date(ano, mes - 1, dia);

            const mesIndex = data.getMonth(); // 0–11
            const valor = parseFloat(caixa.valor_pago) || 0;

            receitaPorMes[mesIndex] += valor;
          } catch (e) {
            console.error("Erro ao processar caixa (gráfico):", e);
          }
        }
      });

    return receitaPorMes;
  };

  // Calcular despesas por mês para gráfico
  const calcularDespesasPorMes = (despesas) => {
    const despesaPorMes = Array(12).fill(0);

    despesas
      .filter((d) => d.ativo === "1")
      .forEach((desp) => {
        if (desp.data_cadastro && desp.valor) {
          try {
            let dataStr = desp.data_cadastro;

            // trata: '2026-01-24', '2026-01-24 00:25:14'
            if (dataStr.includes(" ")) {
              dataStr = dataStr.split(" ")[0];
            }

            const [ano, mes, dia] = dataStr.split("-").map(Number);
            const data = new Date(ano, mes - 1, dia);

            const mesIndex = data.getMonth();
            const valor = parseFloat(desp.valor) || 0;

            despesaPorMes[mesIndex] += valor;
          } catch (e) {
            console.error("Erro ao processar despesa (gráfico):", e);
          }
        }
      });

    return despesaPorMes;
  };

  // Dados do gráfico com cores ajustadas
  const revenueChartData = useMemo(
    () => ({
      labels: [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez",
      ],
      datasets: [
        {
          label: "Receitas",
          backgroundColor: "#2ECC71",
          borderColor: "#27AE60",
          borderWidth: 2,
          borderRadius: 4,
          data: calcularReceitasPorMes(caixasFiltrados),
        },
        {
          label: "Despesas",
          backgroundColor: "#E74C3C",
          borderColor: "#C0392B",
          borderWidth: 2,
          borderRadius: 4,
          data: calcularDespesasPorMes(despesasFiltradas),
        },
      ],
    }),
    [caixasFiltrados, despesasFiltradas],
  );

  // Formatação
  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    try {
      let dataLimpa = dateString;

      if (dataLimpa.includes("T")) {
        dataLimpa = dataLimpa.split("T")[0];
      } else if (dataLimpa.includes(" ")) {
        dataLimpa = dataLimpa.split(" ")[0];
      }

      const [ano, mes, dia] = dataLimpa.split("-").map(Number);
      const data = new Date(ano, mes - 1, dia);

      return data.toLocaleDateString("pt-BR");
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "-";
    try {
      const [hours, minutes] = timeString.split(":");
      return `${hours}:${minutes}`;
    } catch (e) {
      return timeString;
    }
  };

  // Adicione estas funções perto das outras funções utilitárias (após handleSelecionarDia):
  const irParaDiaAnterior = () => {
    const novoDia = new Date(dataInicio);
    novoDia.setDate(novoDia.getDate() - 1);

    setDataInicio(novoDia);
    setDataFim(
      new Date(
        novoDia.getFullYear(),
        novoDia.getMonth(),
        novoDia.getDate(),
        23,
        59,
        59,
      ),
    );
  };

  const irParaProximoDia = () => {
    const novoDia = new Date(dataInicio);
    novoDia.setDate(novoDia.getDate() + 1);

    setDataInicio(novoDia);
    setDataFim(
      new Date(
        novoDia.getFullYear(),
        novoDia.getMonth(),
        novoDia.getDate(),
        23,
        59,
        59,
      ),
    );
  };

  const irParaHoje = () => {
    const hoje = new Date();
    setDataInicio(hoje);
    setDataFim(
      new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 23, 59, 59),
    );
  };

  // Processar dados financeiros consolidados
  const financeiroConsolidado = useMemo(() => {
    if (!Array.isArray(caixasFiltrados)) return [];

    // Criar array para consolidar informações
    const consolidado = [];

    caixasFiltrados.forEach((caixa) => {
      if (!Array.isArray(caixa?.pagamentos) || caixa.pagamentos.length === 0) {
        return;
      }

      // Para cada pagamento no caixa, criar um item
      caixa.pagamentos.forEach((pagamento) => {
        consolidado.push({
          ...caixa,
          pagamento_id: pagamento.id,
          forma_pagamento_id: pagamento.forma_pagamento_id,
          forma_pagamento_descricao: pagamento.forma_pagamento_descricao,
          valor_pagamento: parseFloat(pagamento.valor) || 0,
          data_pagamento: pagamento.data_pagamento,
          usuario_pagamento: pagamento.usuario_nome,
          // Manter informações originais
          valor_total: parseFloat(caixa.valor_total) || 0,
          valor_pago: parseFloat(caixa.valor_pago) || 0,
          valor_pendente: parseFloat(caixa.valor_pendente) || 0,
        });
      });
    });

    return consolidado.sort(
      (a, b) => new Date(b.data_pagamento) - new Date(a.data_pagamento),
    );
  }, [caixasFiltrados]);

  // Calcular resumo por forma de pagamento
  const resumoFormasPagamento = useMemo(() => {
    if (!Array.isArray(financeiroConsolidado)) return [];

    const formasMap = {};

    financeiroConsolidado.forEach((item) => {
      const forma = item.forma_pagamento_descricao || "Não informado";

      if (!formasMap[forma]) {
        formasMap[forma] = {
          forma: forma,
          quantidade: 0,
          valor_total: 0,
          atendimentos: new Set(),
        };
      }

      formasMap[forma].quantidade++;
      formasMap[forma].valor_total += item.valor_pagamento;
      formasMap[forma].atendimentos.add(item.id); // Usar id do caixa para contar atendimentos únicos
    });

    // Converter para array e calcular média
    return Object.values(formasMap)
      .map((forma) => ({
        ...forma,
        atendimentos: forma.atendimentos.size,
        valor_medio: forma.valor_total / forma.quantidade,
      }))
      .sort((a, b) => b.valor_total - a.valor_total);
  }, [financeiroConsolidado]);

  // Calcular totais gerais
  const totalFinanceiro = useMemo(() => {
    if (!Array.isArray(financeiroConsolidado)) {
      return {
        quantidade_pagamentos: 0,
        valor_total: 0,
        quantidade_atendimentos: 0,
        formas_pagamento: 0,
      };
    }

    const atendimentosUnicos = new Set(
      financeiroConsolidado.map((item) => item.id),
    );
    const formasUnicas = new Set(
      financeiroConsolidado.map((item) => item.forma_pagamento_descricao),
    );

    return {
      quantidade_pagamentos: financeiroConsolidado.length,
      valor_total: financeiroConsolidado.reduce(
        (sum, item) => sum + item.valor_pagamento,
        0,
      ),
      quantidade_atendimentos: atendimentosUnicos.size,
      formas_pagamento: formasUnicas.size,
    };
  }, [financeiroConsolidado]);

  // Cards de resumo com gradientes
  const cardsResumo = [
    {
      id: "receitas",
      titulo: "Receitas",
      valor: totalReceitas,
      icone: <FaPlusCircle style={{ fontSize: "1.8rem", color: "#fff" }} />,
      corGradiente: "linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)",
      descricao: "Total de receitas no período",
    },
    {
      id: "despesas",
      titulo: "Despesas",
      valor: totalDespesas,
      icone: <FaMinusCircle style={{ fontSize: "1.8rem", color: "#fff" }} />,
      corGradiente: "linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)",
      descricao: "Total de despesas no período",
    },
    {
      id: "saldo",
      titulo: "Saldo",
      valor: saldoFinal,
      icone: <FaExchangeAlt style={{ fontSize: "1.8rem", color: "#fff" }} />,
      corGradiente:
        saldoFinal >= 0
          ? "linear-gradient(135deg, #3498DB 0%, #2980B9 100%)"
          : "linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)",
      descricao: saldoFinal >= 0 ? "Saldo positivo" : "Saldo negativo",
    },
    {
      id: "carteira",
      titulo: "Carteira",
      valor: totalCarteiraValor,
      icone: <FaWallet style={{ fontSize: "1.8rem", color: "#fff" }} />,
      corGradiente: "linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)",
      descricao: `${totalPagamentosCarteira} pagamentos em carteira`,
    },
  ];

  const handleImprimir = () => {
    // Criar uma nova janela para impressão
    const printWindow = window.open("", "_blank");

    // Função para agrupar dados por empresa
    const agruparPorEmpresa = (dados) => {
      const empresasMap = new Map();

      dados.forEach((item) => {
        const empresaNome = item.empresa_nome || "Sem Empresa";
        if (!empresasMap.has(empresaNome)) {
          empresasMap.set(empresaNome, []);
        }
        empresasMap.get(empresaNome).push(item);
      });

      return empresasMap;
    };

    // Agrupar todos os dados por empresa
    const agendamentosPorEmpresa = agruparPorEmpresa(agendamentosFiltrados);
    const financeiroPorEmpresa = agruparPorEmpresa(financeiroConsolidado);
    const carteiraPorEmpresa = agruparPorEmpresa(carteiraFiltrados);
    const despesasPorEmpresa = agruparPorEmpresa(despesasFiltradas);

    // Calcular totais por empresa
    const calcularTotaisPorEmpresa = (empresaNome) => {
      const receitas = financeiroConsolidado
        .filter((item) => (item.empresa_nome || "Sem Empresa") === empresaNome)
        .reduce((sum, item) => sum + item.valor_pagamento, 0);

      const despesas = despesasFiltradas
        .filter((item) => (item.empresa_nome || "Sem Empresa") === empresaNome)
        .reduce((sum, item) => sum + (parseFloat(item.valor) || 0), 0);

      return { receitas, despesas, saldo: receitas - despesas };
    };

    // Estilos específicos para impressão
    const styles = `
    <style>
      @media print {
        body {
          font-family: 'Rajdhani', Arial, sans-serif;
          margin: 0;
          padding: 15px;
          background: white;
          color: #333;
          font-size: 12px;
        }
        
        .print-header {
          text-align: center;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #B83732;
        }

        .logo-container {
          text-align: center;
          margin-bottom: 15px;
        }
        
        .logo {
          width: 200px;
          height: auto;
        }
        
        .print-header h1 {
          color: #B83732;
          margin: 0;
          font-size: 20px;
        }
        
        .print-periodo {
          text-align: center;
          margin: 15px 0 20px;
          padding: 8px;
          background: #f5f5f5;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 600;
          color: #B83732;
        }
        
        .empresa-section {
          margin-bottom: 10px;
          page-break-after: always;
        }
        
        .empresa-section:last-child {
          page-break-after: auto;
        }
        
        .empresa-titulo {
          background: #B83732;
          color: white;
          padding: 10px;
          margin: 10px 0 15px 0;
          border-radius: 4px;
          font-size: 16px;
          font-weight: 600;
          text-align: center;
        }
        
        .print-tabela {
          margin-bottom: 25px;
          page-break-inside: avoid;
        }
        
        .print-tabela h3 {
          color: #B83732;
          border-bottom: 2px solid #B83732;
          padding-bottom: 5px;
          margin: 0 0 10px 0;
          font-size: 14px;
          font-weight: 600;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }
        
        th {
          background: #B83732;
          color: white;
          padding: 6px 8px;
          text-align: left;
          font-weight: 600;
          font-size: 11px;
        }
        
        td {
          padding: 6px 8px;
          border-bottom: 1px solid #ddd;
        }
        
        tr:nth-child(even) {
          background: #f9f9f9;
        }
        
        .status-badge {
          display: inline-block;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 10px;
          font-weight: 600;
        }
        
        .print-totais {
          margin: 30px auto;
          padding: 15px;
          background: #f5f5f5;
          border-radius: 4px;
          text-align: center;
          font-size: 14px;
          font-weight: 600;
          border: 1px solid #ddd;
          max-width: 400px;
        }
        
        .print-totais div {
          margin-bottom: 8px;
        }
        
        .print-totais div:last-child {
          margin-bottom: 0;
          font-size: 16px;
          padding-top: 8px;
          border-top: 1px solid #ccc;
        }
        
        .print-footer {
          margin-top: 20px;
          text-align: center;
          color: #666;
          font-size: 10px;
          border-top: 1px solid #ddd;
          padding-top: 10px;
        }
        
        .page-break {
          page-break-before: always;
        }
        
        .valor-positivo {
          color: #27AE60;
          font-weight: 700;
        }
        
        .valor-negativo {
          color: #E74C3C;
          font-weight: 700;
        }
        
        .valor-destaque {
          font-weight: 700;
        }

        .resumo-empresa {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin: 15px 0;
        }

        .resumo-card {
          background: #f8f9f9;
          padding: 10px;
          border-radius: 4px;
          text-align: center;
          border: 1px solid #ddd;
        }

        .resumo-card .label {
          font-size: 11px;
          color: #666;
          margin-bottom: 5px;
        }

        .resumo-card .value {
          font-size: 14px;
          font-weight: 700;
        }
      }
      
      /* Estilos para tela (pré-visualização) */
      body {
        font-family: 'Rajdhani', Arial, sans-serif;
        margin: 0;
        padding: 15px;
        background: white;
        font-size: 12px;
      }
      
      .print-header {
        text-align: center;
        margin-bottom: 20px;
        padding-bottom: 10px;
        border-bottom: 2px solid #B83732;
      }

      .logo-container {
        text-align: center;
        margin-bottom: 15px;
      }
      
      .logo {
        width: 200px;
        height: auto;
      }
      
      .print-header h1 {
        color: #B83732;
        margin: 0;
        font-size: 20px;
      }
      
      .print-periodo {
        text-align: center;
        margin: 15px 0 20px;
        padding: 8px;
        background: #f5f5f5;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 600;
        color: #B83732;
      }
      
      .empresa-section {
        margin-bottom: 10px;
        page-break-after: always;
      }
      
      .empresa-section:last-child {
        page-break-after: auto;
      }
      
      .empresa-titulo {
        background: #B83732;
        color: white;
        padding: 10px;
        margin: 10px 0 15px 0;
        border-radius: 4px;
        font-size: 16px;
        font-weight: 600;
        text-align: center;
      }
      
      .print-tabela {
        margin-bottom: 25px;
      }
      
      .print-tabela h3 {
        color: #B83732;
        border-bottom: 2px solid #B83732;
        padding-bottom: 5px;
        margin: 0 0 10px 0;
        font-size: 14px;
        font-weight: 600;
      }
      
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 11px;
      }
      
      th {
        background: #B83732;
        color: white;
        padding: 6px 8px;
        text-align: left;
        font-weight: 600;
        font-size: 11px;
      }
      
      td {
        padding: 6px 8px;
        border-bottom: 1px solid #ddd;
      }
      
      tr:nth-child(even) {
        background: #f9f9f9;
      }
      
      .status-badge {
        display: inline-block;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 10px;
        font-weight: 600;
      }
      
      .print-totais {
        margin: 30px auto;
        padding: 15px;
        background: #f5f5f5;
        border-radius: 4px;
        text-align: center;
        font-size: 14px;
        font-weight: 600;
        border: 1px solid #ddd;
        max-width: 400px;
      }
      
      .print-totais div {
        margin-bottom: 8px;
      }
      
      .print-totais div:last-child {
        margin-bottom: 0;
        font-size: 16px;
        padding-top: 8px;
        border-top: 1px solid #ccc;
      }
      
      .print-footer {
        margin-top: 20px;
        text-align: center;
        color: #666;
        font-size: 10px;
        border-top: 1px solid #ddd;
        padding-top: 10px;
      }
      
      .valor-positivo {
        color: #27AE60;
        font-weight: 700;
      }
      
      .valor-negativo {
        color: #E74C3C;
        font-weight: 700;
      }
      
      .valor-destaque {
        font-weight: 700;
      }

      .resumo-empresa {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 10px;
        margin: 15px 0;
      }

      .resumo-card {
        background: #f8f9f9;
        padding: 10px;
        border-radius: 4px;
        text-align: center;
        border: 1px solid #ddd;
      }

      .resumo-card .label {
        font-size: 11px;
        color: #666;
        margin-bottom: 5px;
      }

      .resumo-card .value {
        font-size: 14px;
        font-weight: 700;
      }
    </style>
  `;

    // Função para gerar HTML de uma seção por empresa
    const gerarSecaoEmpresa = (empresaNome, dados) => {
      const totais = calcularTotaisPorEmpresa(empresaNome);
      const agendamentosEmpresa = agendamentosPorEmpresa.get(empresaNome) || [];
      const financeiroEmpresa = financeiroPorEmpresa.get(empresaNome) || [];
      const carteiraEmpresa = carteiraPorEmpresa.get(empresaNome) || [];
      const despesasEmpresa = despesasPorEmpresa.get(empresaNome) || [];

      return `
      <div class="empresa-section">
        <div class="empresa-titulo">${empresaNome}</div>
        
        <!-- Resumo da Empresa -->
        <div class="resumo-empresa">
          <div class="resumo-card">
            <div class="label">Receitas</div>
            <div class="value valor-positivo">${formatCurrency(totais.receitas)}</div>
          </div>
          <div class="resumo-card">
            <div class="label">Despesas</div>
            <div class="value valor-negativo">${formatCurrency(totais.despesas)}</div>
          </div>
          <div class="resumo-card">
            <div class="label">Saldo</div>
            <div class="value ${totais.saldo >= 0 ? "valor-positivo" : "valor-negativo"}">${formatCurrency(totais.saldo)}</div>
          </div>
        </div>
        
        <!-- AGENDA -->
        <div class="print-tabela">
          <h3>AGENDA (${agendamentosEmpresa.length} registros)</h3>
          <table>
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Data</th>
                <th>Horário</th>
                <th>Valor</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${agendamentosEmpresa
                .map(
                  (item) => `
                <tr>
                  <td>${item.paciente_nome || "-"}</td>
                  <td>${formatDate(item.data_atendimento)}</td>
                  <td>${formatTime(item.horario)}</td>
                  <td>${formatCurrency(item.servico_valor || 0)}</td>
                  <td>
                    <span class="status-badge" style="
                      background: ${
                        item.status === "realizado"
                          ? "#EAFAF1"
                          : item.status === "confirmado"
                            ? "#E7F3FF"
                            : item.status === "agendado"
                              ? "#F3E8FF"
                              : "#FDEDEC"
                      };
                      color: ${
                        item.status === "realizado"
                          ? "#27AE60"
                          : item.status === "confirmado"
                            ? "#3498DB"
                            : item.status === "agendado"
                              ? "#9B59B6"
                              : "#E74C3C"
                      };
                    ">
                      ${
                        item.status === "realizado"
                          ? "Finalizado"
                          : item.status === "confirmado"
                            ? "Agendado"
                            : item.status === "agendado"
                              ? "Reservado"
                              : "Cancelado"
                      }
                    </span>
                  </td>
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
        </div>
        
        <!-- FINANCEIRO CONSOLIDADO -->
        <div class="print-tabela">
          <h3>FINANCEIRO CONSOLIDADO (${financeiroEmpresa.length} pagamentos)</h3>
          <table>
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Data</th>
                <th>Valor</th>
                <th>Forma de Pagamento</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${financeiroEmpresa
                .map(
                  (item) => `
                <tr>
                  <td>${item.paciente_nome || "-"}</td>
                  <td>${formatDate(item.data_pagamento)}</td>
                  <td class="valor-destaque">${formatCurrency(item.valor_pagamento)}</td>
                  <td>${item.forma_pagamento_descricao || "-"}</td>
                  <td>
                    <span class="status-badge" style="
                      background: ${
                        item.status_pagamento === "pago_total"
                          ? "#EAFAF1"
                          : item.status_pagamento === "pago_parcial"
                            ? "#FEF9E7"
                            : "#FDEDEC"
                      };
                      color: ${
                        item.status_pagamento === "pago_total"
                          ? "#27AE60"
                          : item.status_pagamento === "pago_parcial"
                            ? "#F39C12"
                            : "#E74C3C"
                      };
                    ">
                      ${
                        item.status_pagamento === "pago_total"
                          ? "Pago Total"
                          : item.status_pagamento === "pago_parcial"
                            ? "Pago Parcial"
                            : "Pendente"
                      }
                    </span>
                  </td>
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
        </div>
        
        <!-- PAGAMENTOS EM CARTEIRA -->
        ${
          carteiraEmpresa.length > 0
            ? `
        <div class="print-tabela">
          <h3>PAGAMENTOS EM CARTEIRA (${carteiraEmpresa.length} registros)</h3>
          <table>
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Data</th>
                <th>Horário</th>
                <th>Valor Carteira</th>
              </tr>
            </thead>
            <tbody>
              ${carteiraEmpresa
                .map((item) => {
                  const valorCarteira = item.pagamentos
                    .filter(
                      (p) =>
                        p.forma_pagamento_descricao?.toLowerCase() ===
                        "carteira",
                    )
                    .reduce((sum, p) => sum + (parseFloat(p.valor) || 0), 0);
                  return `
                  <tr>
                    <td>${item.paciente_nome || "-"}</td>
                    <td>${formatDate(item.data_atendimento)}</td>
                    <td>${formatTime(item.horario)}</td>
                    <td class="valor-destaque">${formatCurrency(valorCarteira)}</td>
                  </tr>
                `;
                })
                .join("")}
            </tbody>
          </table>
        </div>
        `
            : ""
        }
        
        <!-- DESPESAS -->
        ${
          despesasEmpresa.length > 0
            ? `
        <div class="print-tabela">
          <h3>DESPESAS (${despesasEmpresa.length} registros)</h3>
          <table>
            <thead>
              <tr>
                <th>Descrição</th>
                <th>Categoria</th>
                <th>Data</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              ${despesasEmpresa
                .map(
                  (item) => `
                <tr>
                  <td>${item.descricao || "-"}</td>
                  <td>${item.categoria || "-"}</td>
                  <td>${formatDate(item.data_cadastro)}</td>
                  <td class="valor-negativo">${formatCurrency(item.valor || 0)}</td>
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
        </div>
        `
            : ""
        }
      </div>
    `;
    };

    // Obter lista única de empresas
    const todasEmpresas = new Set();
    agendamentosFiltrados.forEach((item) =>
      todasEmpresas.add(item.empresa_nome || "Sem Empresa"),
    );
    financeiroConsolidado.forEach((item) =>
      todasEmpresas.add(item.empresa_nome || "Sem Empresa"),
    );
    carteiraFiltrados.forEach((item) =>
      todasEmpresas.add(item.empresa_nome || "Sem Empresa"),
    );
    despesasFiltradas.forEach((item) =>
      todasEmpresas.add(item.empresa_nome || "Sem Empresa"),
    );

    const empresasOrdenadas = Array.from(todasEmpresas).sort();

    // Conteúdo HTML para impressão
    const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Relatório por Empresa - ${dataInicio.toLocaleDateString("pt-BR")} a ${dataFim.toLocaleDateString("pt-BR")}</title>
      <link href="https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&display=swap" rel="stylesheet">
      ${styles}
    </head>
    <body>
      <div class="logo-container">
        <img
          src="/wp-content/plugins/App/build/Logo.png"
          alt="Logo"
          class="logo"
        />
      </div>
      
      <div class="print-header">
        <h1>RELATÓRIO POR EMPRESA</h1>
      </div>
      
      <div class="print-periodo">
        Período: ${dataInicio.toLocaleDateString("pt-BR")} a ${dataFim.toLocaleDateString("pt-BR")}
      </div>
      
      <!-- RELATÓRIO GERAL (primeiro) -->
      <div class="empresa-section">
        <div class="empresa-titulo">RELATÓRIO GERAL - TODAS AS EMPRESAS</div>
        
        <!-- RESUMO POR FORMA DE PAGAMENTO -->
        <div class="print-tabela">
          <h3>RESUMO POR FORMA DE PAGAMENTO</h3>
          <table>
            <thead>
              <tr>
                <th>Forma de Pagamento</th>
                <th>Quantidade</th>
                <th>Valor Total</th>
                <th>Média por Pagamento</th>
              </tr>
            </thead>
            <tbody>
              ${resumoFormasPagamento
                .map(
                  (forma) => `
                <tr>
                  <td><strong>${forma.forma}</strong></td>
                  <td>${forma.quantidade} ${forma.quantidade === 1 ? "pagamento" : "pagamentos"}</td>
                  <td class="valor-destaque">${formatCurrency(forma.valor_total)}</td>
                  <td>${formatCurrency(forma.valor_medio)}</td>
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
        </div>
        
        <!-- AGENDA GERAL -->
        <div class="print-tabela">
          <h3>AGENDA GERAL (${agendamentosFiltrados.length} registros)</h3>
          <table>
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Paciente</th>
                <th>Data</th>
                <th>Horário</th>
                <th>Valor</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${agendamentosFiltrados
                .map(
                  (item) => `
                <tr>
                  <td><strong>${item.empresa_nome || "-"}</strong></td>
                  <td>${item.paciente_nome || "-"}</td>
                  <td>${formatDate(item.data_atendimento)}</td>
                  <td>${formatTime(item.horario)}</td>
                  <td>${formatCurrency(item.servico_valor || 0)}</td>
                  <td>
                    <span class="status-badge" style="
                      background: ${
                        item.status === "realizado"
                          ? "#EAFAF1"
                          : item.status === "confirmado"
                            ? "#E7F3FF"
                            : item.status === "agendado"
                              ? "#F3E8FF"
                              : "#FDEDEC"
                      };
                      color: ${
                        item.status === "realizado"
                          ? "#27AE60"
                          : item.status === "confirmado"
                            ? "#3498DB"
                            : item.status === "agendado"
                              ? "#9B59B6"
                              : "#E74C3C"
                      };
                    ">
                      ${
                        item.status === "realizado"
                          ? "Finalizado"
                          : item.status === "confirmado"
                            ? "Agendado"
                            : item.status === "agendado"
                              ? "Reservado"
                              : "Cancelado"
                      }
                    </span>
                  </td>
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
        </div>
        
        <!-- FINANCEIRO CONSOLIDADO GERAL -->
        <div class="print-tabela">
          <h3>FINANCEIRO CONSOLIDADO GERAL (${financeiroConsolidado.length} pagamentos)</h3>
          <table>
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Paciente</th>
                <th>Data</th>
                <th>Valor</th>
                <th>Forma</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${financeiroConsolidado
                .map(
                  (item) => `
                <tr>
                  <td><strong>${item.empresa_nome || "-"}</strong></td>
                  <td>${item.paciente_nome || "-"}</td>
                  <td>${formatDate(item.data_pagamento)}</td>
                  <td class="valor-destaque">${formatCurrency(item.valor_pagamento)}</td>
                  <td>${item.forma_pagamento_descricao || "-"}</td>
                  <td>
                    <span class="status-badge" style="
                      background: ${
                        item.status_pagamento === "pago_total"
                          ? "#EAFAF1"
                          : item.status_pagamento === "pago_parcial"
                            ? "#FEF9E7"
                            : "#FDEDEC"
                      };
                      color: ${
                        item.status_pagamento === "pago_total"
                          ? "#27AE60"
                          : item.status_pagamento === "pago_parcial"
                            ? "#F39C12"
                            : "#E74C3C"
                      };
                    ">
                      ${
                        item.status_pagamento === "pago_total"
                          ? "Pago Total"
                          : item.status_pagamento === "pago_parcial"
                            ? "Pago Parcial"
                            : "Pendente"
                      }
                    </span>
                  </td>
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
        </div>
        
        <!-- PAGAMENTOS EM CARTEIRA GERAL -->
        ${
          carteiraFiltrados.length > 0
            ? `
        <div class="print-tabela">
          <h3>PAGAMENTOS EM CARTEIRA GERAL (${carteiraFiltrados.length} registros)</h3>
          <table>
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Paciente</th>
                <th>Data</th>
                <th>Horário</th>
                <th>Valor Carteira</th>
              </tr>
            </thead>
            <tbody>
              ${carteiraFiltrados
                .map((item) => {
                  const valorCarteira = item.pagamentos
                    .filter(
                      (p) =>
                        p.forma_pagamento_descricao?.toLowerCase() ===
                        "carteira",
                    )
                    .reduce((sum, p) => sum + (parseFloat(p.valor) || 0), 0);
                  return `
                  <tr>
                    <td><strong>${item.empresa_nome || "-"}</strong></td>
                    <td>${item.paciente_nome || "-"}</td>
                    <td>${formatDate(item.data_atendimento)}</td>
                    <td>${formatTime(item.horario)}</td>
                    <td class="valor-destaque">${formatCurrency(valorCarteira)}</td>
                  </tr>
                `;
                })
                .join("")}
            </tbody>
          </table>
        </div>
        `
            : ""
        }
        
        <!-- DESPESAS GERAIS -->
        ${
          despesasFiltradas.length > 0
            ? `
        <div class="print-tabela">
          <h3>DESPESAS GERAIS (${despesasFiltradas.length} registros)</h3>
          <table>
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Descrição</th>
                <th>Categoria</th>
                <th>Data</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              ${despesasFiltradas
                .map(
                  (item) => `
                <tr>
                  <td><strong>${item.empresa_nome || "-"}</strong></td>
                  <td>${item.descricao || "-"}</td>
                  <td>${item.categoria || "-"}</td>
                  <td>${formatDate(item.data_cadastro)}</td>
                  <td class="valor-negativo">${formatCurrency(item.valor || 0)}</td>
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
        </div>
        `
            : ""
        }
        
        <!-- RESUMO E TOTAIS GERAIS -->
        <div class="print-totais">
          <div>Total de Receitas: <span class="valor-positivo">${formatCurrency(totalReceitas)}</span></div>
          <div>Total de Despesas: <span class="valor-negativo">${formatCurrency(totalDespesas)}</span></div>
          <div>
            Saldo Final: <span class="${saldoFinal >= 0 ? "valor-positivo" : "valor-negativo"}">${formatCurrency(saldoFinal)}</span>
          </div>
        </div>
      </div>
      
      <!-- RELATÓRIOS POR EMPRESA -->
      ${empresasOrdenadas.map((empresa) => gerarSecaoEmpresa(empresa)).join("")}
      
      <div class="print-footer">
        <div>Relatório gerado em ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}</div>
      </div>
      
      <script>
        window.onload = function() {
          window.print();
          window.onafterprint = function() {
            window.close();
          };
        }
      </script>
    </body>
    </html>
  `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  {
    /* Return Principal */
  }
  return (
    <div
      style={{
        fontFamily: "'Rajdhani', sans-serif",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      {/* Barra superior vermelha */}
      <div
        style={{
          backgroundColor: "#B83732",
          height: "40px",
          minHeight: "40px",
          width: "100%",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          fontWeight: "bold",
          position: "fixed",
          zIndex: 1,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        {/* lado esquerdo */}
        <div></div>

        {/* Centro */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Button
            icon="pi pi-refresh"
            className="icon-button-titulo"
            onClick={() => {
              fetchAllData();
              irParaHoje();
            }}
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            outlined
          />
          <Button
            icon={mostrarFiltro ? "pi pi-filter-slash" : "pi pi-filter"}
            className="icon-button-titulo"
            onClick={() => setMostrarFiltro(!mostrarFiltro)}
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            outlined
          />
          <Button
            icon={"pi pi-print"}
            className="icon-button-titulo"
            onClick={handleImprimir}
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            outlined
          />
        </div>

        {/* lado direito */}
        <div></div>
      </div>

      {/* Conteúdo abaixo da barra */}
      <div style={{ flex: 1, padding: "50px 10px" }}>
        <div style={{ borderRadius: "8px", padding: "0 15px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <FaChartLine style={{ color: "#B83732", fontSize: "1.2rem" }} />
              <h3 style={{ margin: 0, color: "#424242" }}>RELATÓRIOS</h3>
            </div>
            <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
              <Button
                icon="pi pi-chevron-left"
                className="p-button-text p-button-sm"
                onClick={irParaDiaAnterior}
                style={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  color: "#424242",
                }}
              />
              <h3 style={{ margin: 0, color: "#424242" }}>
                {dataInicio.toLocaleDateString("pt-BR")}
              </h3>
              <Button
                icon="pi pi-chevron-right"
                className="p-button-text p-button-sm"
                onClick={irParaProximoDia}
                style={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  color: "#424242",
                }}
              />
            </div>
          </div>
        </div>

        {/* Filtros*/}
        {mostrarFiltro && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "10px",
            }}
          >
            <div
              style={{
                borderRadius: "8px",
                padding: "10px 8px",
                border: "1px solid #dee2e6",
                backgroundColor: "#fff",
                width: "95%",
                maxWidth: "720px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              {/* 1ª Linha - Data Inicial, Data Final e Botões */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                }}
              >
                {/* Card Data Inicial */}
                <div style={{ padding: "10px" }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        paddingBottom: "5px",
                        borderBottom: "2px solid #3498DB",
                      }}
                    >
                      <FaCalendar
                        style={{ color: "#3498DB", fontSize: "0.9rem" }}
                      />
                      <span
                        style={{
                          fontWeight: "600",
                          color: "#2c3e50",
                          fontSize: "0.95rem",
                          letterSpacing: "0.3px",
                        }}
                      >
                        Data Inicial{" "}
                      </span>
                    </div>
                    <Calendar
                      id="dataInicio"
                      value={dataInicio}
                      onChange={(e) => setDataInicio(e.value)}
                      dateFormat="dd/mm/yy"
                      showIcon
                      style={{
                        outline: "none",
                        boxShadow: "none",
                        width: "100%",
                        height: "35px",
                        borderRadius: "8px",
                        fontFamily: "'Rajdhani', sans-serif",
                        border: "2px solid #E3E6F0",
                        fontSize: "1rem",
                        "&:focus": {
                          borderColor: "#3498DB",
                          boxShadow: "0 0 0 3px rgba(52, 152, 219, 0.2)",
                        },
                      }}
                    />
                  </div>
                </div>

                {/* Card Data Final */}
                <div style={{ padding: "10px" }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        paddingBottom: "5px",
                        borderBottom: "2px solid #d35400",
                      }}
                    >
                      <FaCalendar
                        style={{ color: "#d35400", fontSize: "0.9rem" }}
                      />
                      <span
                        style={{
                          fontWeight: "600",
                          color: "#2c3e50",
                          fontSize: "0.95rem",
                          letterSpacing: "0.3px",
                        }}
                      >
                        Data Final
                      </span>
                    </div>
                    <Calendar
                      id="dataFim"
                      value={dataFim}
                      onChange={(e) => setDataFim(e.value)}
                      dateFormat="dd/mm/yy"
                      showIcon
                      style={{
                        outline: "none",
                        boxShadow: "none",
                        width: "100%",
                        height: "35px",
                        borderRadius: "8px",
                        fontFamily: "'Rajdhani', sans-serif",
                        border: "2px solid #E3E6F0",
                        fontSize: "1rem",
                        "&:focus": {
                          borderColor: "#3498DB",
                          boxShadow: "0 0 0 3px rgba(52, 152, 219, 0.2)",
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
              {/* 1ª Linha - Data Inicial, Data Final e Botões */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap:"5px",
                  padding: "0 10px",
                  alignItems: "center",
                }}
              >
                <Button
                  label="Dia"
                  icon="pi pi-calendar"
                  className="p-button-outlined p-button-sm"
                  style={{
                    outline: "none",
                    boxShadow: "none",
                    fontFamily: "'Rajdhani', sans-serif",
                    padding: "3px 8px",
                    fontSize: "0.75rem",
                    borderColor: "#dee2e6",
                    color: "#495057",
                  }}
                />
                <Button
                  label="Semana"
                  icon="pi pi-calendar"
                  className="p-button-outlined p-button-sm"
                  style={{
                    outline: "none",
                    boxShadow: "none",
                    fontFamily: "'Rajdhani', sans-serif",
                    padding: "3px 8px",
                    fontSize: "0.75rem",
                    borderColor: "#dee2e6",
                    color: "#495057",
                  }}
                />
                <Button
                  label="Mês"
                  icon="pi pi-calendar"
                  className="p-button-outlined p-button-sm"
                  style={{
                    outline: "none",
                    boxShadow: "none",
                    fontFamily: "'Rajdhani', sans-serif",
                    padding: "3px 8px",
                    fontSize: "0.75rem",
                    borderColor: "#dee2e6",
                    color: "#495057",
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Conteúdo principal */}
      <div
        style={{
          padding: "0 25px 25px 25px",
          backgroundColor: "#f5f5f5",
        }}
      >
        {loading && (
          <div
            style={{
              textAlign: "center",
              padding: "100px 20px",
              backgroundColor: "#f5f5f5",
              borderRadius: "15px",
              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
            }}
          >
            <ProgressSpinner
              style={{ width: "50px", height: "50px" }}
              strokeWidth="4"
            />
            <h3
              style={{
                color: "#2c3e50",
                margin: "0",
                fontSize: "1.1rem",
                fontWeight: "500",
              }}
            >
              Carregando relatórios...
            </h3>
          </div>
        )}

        {!loading && (
          <>
            {/* Cards de Resumo com gradientes igual ao Dashboard */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "20px",
                marginBottom: "40px",
              }}
            >
              {cardsResumo.map((card) => (
                <div
                  key={card.id}
                  onMouseEnter={() => setExpandedCard(card.id)}
                  onMouseLeave={() => setExpandedCard(null)}
                  style={{
                    background: card.corGradiente,
                    borderRadius: "15px",
                    padding: "25px 20px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow:
                      expandedCard === card.id
                        ? "0 10px 25px rgba(0,0,0,0.2)"
                        : "0 5px 15px rgba(0,0,0,0.1)",
                    transform:
                      expandedCard === card.id
                        ? "translateY(-5px)"
                        : "translateY(0)",
                    color: "#fff",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "-50%",
                      right: "-50%",
                      width: "200%",
                      height: "200%",
                      background:
                        "radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)",
                      opacity: expandedCard === card.id ? 0.3 : 0,
                      transition: "opacity 0.3s ease",
                      pointerEvents: "none",
                    }}
                  />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "20px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "rgba(255,255,255,0.2)",
                        borderRadius: "12px",
                        padding: "12px",
                        display: "inline-flex",
                      }}
                    >
                      {card.icone}
                    </div>
                    <FaArrowRight
                      style={{
                        fontSize: "1.2rem",
                        opacity: expandedCard === card.id ? 1 : 0.5,
                        transition: "opacity 0.3s ease",
                      }}
                    />
                  </div>

                  <h3
                    style={{
                      fontSize: "1rem",
                      fontWeight: "500",
                      margin: "0 0 5px 0",
                      opacity: 0.9,
                    }}
                  >
                    {card.titulo}
                  </h3>

                  <div
                    style={{
                      fontSize: "2rem",
                      fontWeight: "700",
                      margin: "0 0 5px 0",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {formatCurrency(card.valor)}
                  </div>

                  <p
                    style={{
                      fontSize: "0.85rem",
                      margin: 0,
                      opacity: 0.8,
                      lineHeight: "1.4",
                    }}
                  >
                    {card.descricao}
                  </p>
                </div>
              ))}
            </div>

            {/* Gráfico de Receitas x Despesas */}
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "15px",
                padding: "25px",
                marginBottom: "30px",
                boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                border: "1px solid #e8e8e8",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "20px",
                  borderBottom: "2px solid #9b59b6",
                  paddingBottom: "15px",
                }}
              >
                <FaChartLine style={{ color: "#9b59b6", fontSize: "1.2rem" }} />
                <h3
                  style={{
                    margin: 0,
                    color: "#2c3e50",
                    fontSize: "1.3rem",
                    fontWeight: "600",
                  }}
                >
                  Receitas vs Despesas
                </h3>
              </div>
              <Chart
                type="bar"
                data={revenueChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "top",
                      labels: {
                        font: {
                          family: "'Rajdhani', sans-serif",
                          size: 14,
                          weight: "600",
                        },
                        padding: 20,
                      },
                    },
                    tooltip: {
                      backgroundColor: "#2C3E50",
                      titleFont: {
                        family: "'Rajdhani', sans-serif",
                        size: 13,
                        weight: "600",
                      },
                      bodyFont: {
                        family: "'Rajdhani', sans-serif",
                        size: 13,
                      },
                      padding: 12,
                      cornerRadius: 8,
                      callbacks: {
                        label: function (context) {
                          return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
                        },
                      },
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function (value) {
                          return formatCurrency(value);
                        },
                        font: {
                          family: "'Rajdhani', sans-serif",
                          size: 12,
                          weight: "600",
                        },
                        color: "#5D6D7E",
                      },
                      grid: {
                        color: "#EAEDED",
                        drawBorder: false,
                      },
                    },
                    x: {
                      ticks: {
                        font: {
                          family: "'Rajdhani', sans-serif",
                          size: 12,
                          weight: "600",
                        },
                        color: "#5D6D7E",
                      },
                      grid: {
                        color: "#EAEDED",
                        drawBorder: false,
                      },
                    },
                  },
                }}
                style={{ height: "400px" }}
              />
            </div>

            {/* Cards de Estatísticas rápidas */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "15px",
                marginBottom: "30px",
              }}
            >
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "1px solid #e8e8e8",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 5px 15px rgba(0,0,0,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(0,0,0,0.04)";
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      backgroundColor: "#9b59b6",
                      borderRadius: "10px",
                      padding: "10px",
                      display: "inline-flex",
                    }}
                  >
                    <FaCalendar style={{ color: "#fff", fontSize: "1rem" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: "0.8rem", color: "#7f8c8d" }}>
                      Reservas
                    </div>
                    <div
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "700",
                        color: "#2c3e50",
                      }}
                    >
                      {totalReservas}
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "1px solid #e8e8e8",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 5px 15px rgba(0,0,0,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(0,0,0,0.04)";
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      backgroundColor: "#3498db",
                      borderRadius: "10px",
                      padding: "10px",
                      display: "inline-flex",
                    }}
                  >
                    <FaCalendarCheck
                      style={{ color: "#fff", fontSize: "1rem" }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: "0.8rem", color: "#7f8c8d" }}>
                      Agendas
                    </div>
                    <div
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "700",
                        color: "#2c3e50",
                      }}
                    >
                      {totalAgendas}
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "1px solid #e8e8e8",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 5px 15px rgba(0,0,0,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(0,0,0,0.04)";
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      backgroundColor: "#f39c12",
                      borderRadius: "10px",
                      padding: "10px",
                      display: "inline-flex",
                    }}
                  >
                    <FaClock style={{ color: "#fff", fontSize: "1rem" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: "0.8rem", color: "#7f8c8d" }}>
                      Pendentes
                    </div>
                    <div
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "700",
                        color: "#2c3e50",
                      }}
                    >
                      {totalPendentes}
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "1px solid #e8e8e8",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 5px 15px rgba(0,0,0,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(0,0,0,0.04)";
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      backgroundColor: "#27ae60",
                      borderRadius: "10px",
                      padding: "10px",
                      display: "inline-flex",
                    }}
                  >
                    <FaCheckCircle
                      style={{ color: "#fff", fontSize: "1rem" }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: "0.8rem", color: "#7f8c8d" }}>
                      Finalizados
                    </div>
                    <div
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "700",
                        color: "#2c3e50",
                      }}
                    >
                      {totalFinalizados}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Seção de Tabelas */}
            <div style={{ marginBottom: "30px" }}>
              {/* Tabela de Agenda */}
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "15px",
                  padding: "20px",
                  marginBottom: "20px",
                  boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                  border: "1px solid #e8e8e8",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "20px",
                    borderBottom: "2px solid #f39c12",
                    paddingBottom: "15px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <FaCalendarCheck
                      style={{ color: "#f39c12", fontSize: "1.2rem" }}
                    />
                    <h3
                      style={{
                        margin: 0,
                        color: "#2c3e50",
                        fontSize: "1.2rem",
                        fontWeight: "600",
                      }}
                    >
                      Agenda
                    </h3>
                  </div>
                  <Tag
                    value={agendamentosFiltrados.length}
                    style={{
                      background:
                        "linear-gradient(135deg, #f39c12 0%, #e67e22 100%)",
                      fontWeight: "600",
                      padding: "8px 12px",
                      borderRadius: "20px",
                    }}
                  />
                </div>

                <div style={{ overflowX: "auto" }}>
                  <DataTable
                    value={agendamentosFiltrados}
                    stripedRows
                    paginator
                    rows={10}
                    style={{
                      fontFamily: "'Rajdhani', sans-serif",
                      fontSize: "0.85rem",
                      minWidth: "800px",
                    }}
                    emptyMessage={
                      <div
                        style={{
                          padding: "40px",
                          textAlign: "center",
                          color: "#95A5A6",
                        }}
                      >
                        Nenhum agendamento no período
                      </div>
                    }
                    size="small"
                  >
                    <Column field="paciente_nome" header="Paciente" />
                    <Column field="empresa_nome" header="Empresa" />
                    <Column field="id" header="Ag" />
                    <Column
                      field="data_atendimento"
                      header="Data Marcação"
                      body={(row) => formatDate(row.data_atendimento)}
                    />
                    <Column
                      field="data_fechamento"
                      header="Data Fechamento"
                      body={(row) => formatDate(row.data_fechamento)}
                    />
                    <Column
                      field="horario"
                      header="Horário"
                      body={(row) => formatTime(row.horario)}
                    />
                    <Column
                      header="Valor"
                      body={(row) =>
                        row.servico_valor
                          ? formatCurrency(row.servico_valor)
                          : "-"
                      }
                    />
                    <Column
                      header="Status"
                      body={(row) => {
                        const statusMap = {
                          realizado: {
                            label: "Finalizado",
                            color: "#27AE60",
                            bg: "#EAFAF1",
                          },
                          confirmado: {
                            label: "Agendado",
                            color: "#3498DB",
                            bg: "#E7F3FF",
                          },
                          agendado: {
                            label: "Reservado",
                            color: "#9B59B6",
                            bg: "#F3E8FF",
                          },
                          cancelado: {
                            label: "Cancelado",
                            color: "#E74C3C",
                            bg: "#FDEDEC",
                          },
                        };
                        const status = statusMap[row.status] || {
                          label: "Pendente",
                          color: "#F39C12",
                          bg: "#FEF9E7",
                        };
                        return (
                          <div
                            style={{
                              backgroundColor: status.bg,
                              color: status.color,
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontWeight: "600",
                              fontSize: "0.75rem",
                              textAlign: "center",
                              display: "inline-block",
                            }}
                          >
                            {status.label}
                          </div>
                        );
                      }}
                    />
                  </DataTable>
                </div>
              </div>

              {/* Resumo por Forma de Pagamento */}
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "15px",
                  padding: "20px",
                  marginBottom: "20px",
                  boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                  border: "1px solid #e8e8e8",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "20px",
                    borderBottom: "2px solid #2ecc71",
                    paddingBottom: "15px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <FaMoneyBillWave
                      style={{ color: "#2ecc71", fontSize: "1.2rem" }}
                    />
                    <h3
                      style={{
                        margin: 0,
                        color: "#2c3e50",
                        fontSize: "1.2rem",
                        fontWeight: "600",
                      }}
                    >
                      Forma de Pagamento
                    </h3>
                  </div>
                  <Tag
                    value={`${resumoFormasPagamento.length} formas`}
                    style={{
                      background:
                        "linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)",
                      fontWeight: "600",
                      padding: "8px 12px",
                      borderRadius: "20px",
                    }}
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "15px",
                  }}
                >
                  {resumoFormasPagamento.map((forma, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor: "#f8f9f9",
                        borderRadius: "10px",
                        padding: "15px",
                        border: "1px solid #e8e8e8",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 5px 15px rgba(0,0,0,0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "10px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "0.9rem",
                            fontWeight: "700",
                            color: "#2c3e50",
                          }}
                        >
                          {forma.forma}
                        </span>
                        <span
                          style={{
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            color: "#27ae60",
                            backgroundColor: "#eafaf1",
                            padding: "4px 8px",
                            borderRadius: "4px",
                          }}
                        >
                          {forma.quantidade}x
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "1.2rem",
                          fontWeight: "bold",
                          color: "#27ae60",
                          textAlign: "right",
                        }}
                      >
                        {formatCurrency(forma.valor_total)}
                      </div>
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#7f8c8d",
                          marginTop: "5px",
                          textAlign: "right",
                        }}
                      >
                        Média: {formatCurrency(forma.valor_medio)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tabela de Financeiro */}
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "15px",
                  padding: "20px",
                  marginBottom: "20px",
                  boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                  border: "1px solid #e8e8e8",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "20px",
                    borderBottom: "2px solid #2ecc71",
                    paddingBottom: "15px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <FaMoneyBillWave
                      style={{ color: "#2ecc71", fontSize: "1.2rem" }}
                    />
                    <h3
                      style={{
                        margin: 0,
                        color: "#2c3e50",
                        fontSize: "1.2rem",
                        fontWeight: "600",
                      }}
                    >
                      Financeiro
                    </h3>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <Tag
                      value={totalFinanceiro.quantidade_atendimentos}
                      style={{
                        background:
                          "linear-gradient(135deg, #3498DB 0%, #2980B9 100%)",
                        fontWeight: "600",
                        padding: "8px 12px",
                        borderRadius: "20px",
                      }}
                    />
                    <Tag
                      value={formatCurrency(totalFinanceiro.valor_total)}
                      style={{
                        background:
                          "linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)",
                        fontWeight: "600",
                        padding: "8px 12px",
                        borderRadius: "20px",
                      }}
                    />
                  </div>
                </div>

                <div style={{ overflowX: "auto" }}>
                  <DataTable
                    value={financeiroConsolidado}
                    stripedRows
                    paginator
                    rows={10}
                    style={{
                      fontFamily: "'Rajdhani', sans-serif",
                      fontSize: "0.85rem",
                      minWidth: "1000px",
                    }}
                    emptyMessage={
                      <div
                        style={{
                          padding: "40px",
                          textAlign: "center",
                          color: "#95A5A6",
                        }}
                      >
                        Nenhum pagamento registrado no período
                      </div>
                    }
                    size="small"
                  >                   
                    <Column field="paciente_nome" header="Paciente" />                    
                    <Column field="empresa_nome" header="Empresa" />
                    <Column field="id" header="Ag" />
                    <Column
                      field="data_pagamento"
                      header="Data"
                      body={(row) => formatDate(row.data_pagamento)}
                    />
                    <Column
                      header="Valor"
                      body={(row) => formatCurrency(row.valor_pagamento)}
                    />
                    <Column
                      header="Forma"
                      body={(row) => {
                        const cores = {
                          Dinheiro: { cor: "#27AE60", bg: "#EAFAF1" },
                          "Cartão de Crédito": {
                            cor: "#3498DB",
                            bg: "#E7F3FF",
                          },
                          "Cartão de Débito": { cor: "#2980B9", bg: "#E3F2FD" },
                          Carteira: { cor: "#9B59B6", bg: "#F3E8FF" },
                          PIX: { cor: "#32CD32", bg: "#F0FFF0" },
                        };
                        const estilo = cores[row.forma_pagamento_descricao] || {
                          cor: "#7F8C8D",
                          bg: "#F8F9F9",
                        };
                        return (
                          <div
                            style={{
                              backgroundColor: estilo.bg,
                              color: estilo.cor,
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontWeight: "600",
                              fontSize: "0.75rem",
                              textAlign: "center",
                              display: "inline-block",
                            }}
                          >
                            {row.forma_pagamento_descricao}
                          </div>
                        );
                      }}
                    />
                    <Column
                      header="Status"
                      body={(row) => {
                        const statusMap = {
                          pago_total: {
                            label: "Pago Total",
                            color: "#27AE60",
                            bg: "#EAFAF1",
                          },
                          pago_parcial: {
                            label: "Pago Parcial",
                            color: "#F39C12",
                            bg: "#FEF9E7",
                          },
                        };
                        const status = statusMap[row.status_pagamento] || {
                          label: "Pendente",
                          color: "#E74C3C",
                          bg: "#FDEDEC",
                        };
                        return (
                          <div
                            style={{
                              backgroundColor: status.bg,
                              color: status.color,
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontWeight: "600",
                              fontSize: "0.75rem",
                              textAlign: "center",
                              display: "inline-block",
                            }}
                          >
                            {status.label}
                          </div>
                        );
                      }}
                    />
                    {/* <Column
                      header="Ações"
                      body={(row) => (
                        <Button
                          icon={<FaPrint />}
                          className="p-button-rounded p-button-info p-button-sm"
                          style={{
                            background: "linear-gradient(135deg, #3498DB 0%, #2980B9 100%)",
                            border: "none",
                            width: "2rem",
                            height: "2rem",
                          }}
                          tooltip="Imprimir recibo"
                          tooltipOptions={{ position: "top" }}
                        />
                      )}
                    /> */}
                  </DataTable>
                </div>
              </div>

              {/* Tabela de Carteira */}
              {carteiraFiltrados.length > 0 && (
                <div
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "15px",
                    padding: "20px",
                    marginBottom: "20px",
                    boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                    border: "1px solid #e8e8e8",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "20px",
                      borderBottom: "2px solid #9b59b6",
                      paddingBottom: "15px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <FaWallet
                        style={{ color: "#9b59b6", fontSize: "1.2rem" }}
                      />
                      <h3
                        style={{
                          margin: 0,
                          color: "#2c3e50",
                          fontSize: "1.2rem",
                          fontWeight: "600",
                        }}
                      >
                        Pagamentos em Carteira
                      </h3>
                    </div>
                    <Tag
                      value={formatCurrency(totalCarteiraValor)}
                      style={{
                        background:
                          "linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)",
                        fontWeight: "600",
                        padding: "8px 12px",
                        borderRadius: "20px",
                      }}
                    />
                  </div>

                  <div style={{ overflowX: "auto" }}>
                    <DataTable
                      value={carteiraFiltrados}
                      stripedRows
                      paginator
                      rows={10}
                      style={{
                        fontFamily: "'Rajdhani', sans-serif",
                        fontSize: "0.85rem",
                        minWidth: "800px",
                      }}
                      emptyMessage={
                        <div
                          style={{
                            padding: "40px",
                            textAlign: "center",
                            color: "#95A5A6",
                          }}
                        >
                          Nenhum pagamento com carteira registrado
                        </div>
                      }
                      size="small"
                    >
                      <Column field="paciente_nome" header="Paciente" />
                      <Column field="empresa_nome" header="Empresa" />
                      <Column
                        field="data_atendimento"
                        header="Data"
                        body={(row) => formatDate(row.data_atendimento)}
                      />
                      <Column
                        field="horario"
                        header="Horário"
                        body={(row) => formatTime(row.horario)}
                      />
                      <Column
                        header="Valor Carteira"
                        body={(row) => {
                          const valorCarteira = row.pagamentos
                            .filter(
                              (p) =>
                                p.forma_pagamento_descricao?.toLowerCase() ===
                                "carteira",
                            )
                            .reduce(
                              (sum, p) => sum + (parseFloat(p.valor) || 0),
                              0,
                            );
                          return (
                            <div
                              style={{
                                color: "#9b59b6",
                                fontWeight: "700",
                              }}
                            >
                              {formatCurrency(valorCarteira)}
                            </div>
                          );
                        }}
                      />
                      {/* <Column
                        header="Ações"
                        body={(row) => {
                          const telefone = row.paciente_telefone;
                          return (
                            <Button
                              icon={<FaWhatsapp />}
                              className="p-button-rounded p-button-success p-button-sm"
                              onClick={() => {
                                if (telefone) {
                                  const phone = telefone.replace(/\D/g, "");
                                  const valorCarteira = row.pagamentos
                                    .filter(
                                      (p) =>
                                        p.forma_pagamento_descricao?.toLowerCase() ===
                                        "carteira",
                                    )
                                    .reduce(
                                      (sum, p) =>
                                        sum + (parseFloat(p.valor) || 0),
                                      0,
                                    );
                                  const mensagem = `Olá ${row.paciente_nome}! Este é um lembrete sobre o pagamento realizado em carteira no valor de ${formatCurrency(valorCarteira)} referente à consulta do dia ${formatDate(row.data_atendimento)}.`;
                                  window.open(
                                    `https://wa.me/55${phone}?text=${encodeURIComponent(mensagem)}`,
                                  );
                                }
                              }}
                              style={{
                                background: telefone
                                  ? "linear-gradient(135deg, #25D366 0%, #128C7E 100%)"
                                  : "#BDC3C7",
                                border: "none",
                                width: "2rem",
                                height: "2rem",
                                cursor: telefone ? "pointer" : "not-allowed",
                              }}
                              tooltip={
                                telefone
                                  ? "Enviar lembrete no WhatsApp"
                                  : "Paciente sem telefone"
                              }
                              tooltipOptions={{ position: "top" }}
                            />
                          );
                        }}
                      /> */}
                    </DataTable>
                  </div>
                </div>
              )}

              {/* Tabela de Despesas */}
              {despesasFiltradas.length > 0 && (
                <div
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "15px",
                    padding: "20px",
                    marginBottom: "20px",
                    boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                    border: "1px solid #e8e8e8",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "20px",
                      borderBottom: "2px solid #e74c3c",
                      paddingBottom: "15px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <FaMinusCircle
                        style={{ color: "#e74c3c", fontSize: "1.2rem" }}
                      />
                      <h3
                        style={{
                          margin: 0,
                          color: "#2c3e50",
                          fontSize: "1.2rem",
                          fontWeight: "600",
                        }}
                      >
                        Despesas
                      </h3>
                    </div>
                    <Tag
                      value={`${despesasFiltradas.length} despesas`}
                      style={{
                        background:
                          "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
                        fontWeight: "600",
                        padding: "8px 12px",
                        borderRadius: "20px",
                      }}
                    />
                  </div>

                  <div style={{ overflowX: "auto" }}>
                    <DataTable
                      value={despesasFiltradas.filter((d) => d.ativo === "1")}
                      stripedRows
                      paginator
                      rows={10}
                      style={{
                        fontFamily: "'Rajdhani', sans-serif",
                        fontSize: "0.85rem",
                        minWidth: "800px",
                      }}
                      emptyMessage={
                        <div
                          style={{
                            padding: "40px",
                            textAlign: "center",
                            color: "#95A5A6",
                          }}
                        >
                          Nenhuma despesa no período
                        </div>
                      }
                      size="small"
                    >
                      <Column field="descricao" header="Descrição" />
                      <Column field="categoria" header="Categoria" />
                      <Column
                        field="data_cadastro"
                        header="Data"
                        body={(row) => formatDate(row.data_cadastro)}
                      />
                      <Column
                        field="valor"
                        header="Valor"
                        body={(row) => (
                          <div
                            style={{
                              color: "#E74C3C",
                              fontWeight: "700",
                            }}
                          >
                            {formatCurrency(row.valor)}
                          </div>
                        )}
                      />
                    </DataTable>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Relatorios;
